import { Box, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import useEmblaCarousel from "embla-carousel-react"

import { Grid, SvgIcon, type FC } from ".."

type WeatherInfo = {
  title: string
  temperature_celsius: string
  temperature_fahrenheit: string
  precipitation_metrics: string
  precipitation_imperial: string
}

export type WeatherProps = {
  items: WeatherInfo[]
}

const Weather: FC<WeatherProps> = ({ items }) => {
  const [emblaRef] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 992px)": {
        active: false,
      },
    },
  })
  return (
    <Grid component="article" sx={{ rowGap: 10 }}>
      <Box gridColumn={{ xs: "1 / -1", lg: "2 / -2" }} columnGap={5} rowGap={5}>
        <WeatherGuideStyledWrapper>
          <Typography
            variant="body.lg"
            component="div"
            py={5}
            mb={{ xs: 10, md: 5 }}
            borderBottom={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
          >
            Weather Guide
          </Typography>
          <Box
            ref={emblaRef}
            sx={{ overflow: { xs: "hidden", lg: "unset" }, p: { xs: 0.5, lg: 0 } }}
          >
            <Box
              display={{ xs: "flex", lg: "grid" }}
              gridTemplateColumns={{
                xs: "unset",
                xl: "repeat(6, minmax(0, 1fr))",
              }}
              gap={5}
              sx={(theme) => ({
                [theme.breakpoints.down("lg")]: {
                  flexDirection: "row",
                  justifyContent: "start",
                },
                [theme.breakpoints.down(1415)]: {
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                },
              })}
            >
              {items.map((item, i) => (
                <WeatherBlock key={i} {...item} />
              ))}
            </Box>
          </Box>
        </WeatherGuideStyledWrapper>
      </Box>
    </Grid>
  )
}

export default Weather

/** A sub component for the month block */
const WeatherBlock: FC<WeatherInfo> = ({
  title,
  temperature_celsius,
  temperature_fahrenheit,
  precipitation_metrics,
  precipitation_imperial,
}) => {
  return (
    <WeatherGuideStyledBlock minWidth={{ xs: 158, lg: "unset" }}>
      <StyledMonthTitle height={80}>
        <Typography px={7} variant="body.sm" component="h2" fontWeight={500} letterSpacing="1.44px">
          {title}
        </Typography>
      </StyledMonthTitle>
      <Stack py={{ xs: 5, lg: 7.5 }} px={{ xs: 4.5, lg: 7 }} rowGap={{ xs: 5, lg: 10 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={5}>
          <SvgIcon variant="sun" size={18} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            columnGap={5}
            flexWrap="wrap"
          >
            {temperature_celsius && (
              <Typography variant="body.sm">{`${temperature_celsius}°C`}</Typography>
            )}
            {temperature_fahrenheit && (
              <Typography variant="body.sm">{`${temperature_fahrenheit}°F`}</Typography>
            )}
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={5}>
          <SvgIcon variant="cloud" size={18} />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            columnGap={5}
            flexWrap="wrap"
          >
            {precipitation_metrics && (
              <Typography variant="body.sm">{`${precipitation_metrics} cm`}</Typography>
            )}
            {precipitation_imperial && (
              <Typography variant="body.sm">{`${precipitation_imperial} in`}</Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </WeatherGuideStyledBlock>
  )
}

/** styled components */
const WeatherGuideStyledBlock = styled(Stack)(
  ({ theme }) => `
    width: 100%;
    box-shadow: -.5px -.5px 0 .5px ${theme.palette["neutral.200"].main}, 1px 1px 2px ${
    theme.palette["neutral.200"].main
  };
    border-radius: ${theme.spacing(1.5)};
  `
)

const StyledMonthTitle = styled(Box)(
  ({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${theme.palette["neutral.200"].main};
  text-transform: uppercase;
  font-size: ${theme.spacing(3)};

`
)

const WeatherGuideStyledWrapper = styled(Box)(
  ({ theme }) => `
    margin-top: ${theme.spacing(10)};
    margin-bottom: ${theme.spacing(10)};
    ${theme.breakpoints.down("md")} {
      margin-top: ${theme.spacing(0)};
    }
  `
)
